import type { PlaybookFeedbackUrlParams } from "@repo/app-types/helpers/playbookFeedbackUrl";
import { createContext, type FC, type ReactNode, useContext } from "react";

export interface PlaybookFeedbackReferenceWrapperProps {
  /** The feedback item the reference points at, as the URL builder takes it. */
  reference: PlaybookFeedbackUrlParams;
  /** Link styling, applied whether or not the reference resolves to a link. */
  className: string;
  children: ReactNode;
}

/**
 * An app-supplied component that turns a playbook feedback reference into a
 * link to the item in its playbook's feedback tab. Apps that don't supply one
 * render references as link-styled text.
 */
export type PlaybookFeedbackReferenceWrapper =
  FC<PlaybookFeedbackReferenceWrapperProps>;

const PlaybookFeedbackReferenceContext = createContext<
  PlaybookFeedbackReferenceWrapper | undefined
>(undefined);

export const PlaybookFeedbackReferenceProvider =
  PlaybookFeedbackReferenceContext.Provider;

/**
 * Renders a playbook feedback reference from the AST as a link: through the
 * host wrapper from `PlaybookFeedbackReferenceProvider` when one is supplied,
 * otherwise as a span with the same link styling. Exists as a component so
 * the plain-function AST renderer can consume the context.
 */
export const PlaybookFeedbackReferenceNode: FC<
  PlaybookFeedbackReferenceWrapperProps
> = (props) => {
  const { reference, className, children } = props;
  const Wrapper = useContext(PlaybookFeedbackReferenceContext);
  if (!Wrapper) {
    return <span className={className}>{children}</span>;
  }
  return (
    <Wrapper reference={reference} className={className}>
      {children}
    </Wrapper>
  );
};
